
import { defineComponent, PropType } from 'vue'

interface ItemHeightCache {
  [key: number]: number
}

interface VirtualScrollerItem {
  [key: string]: string | number | symbol | undefined | unknown
  id?: string | number
}

interface PerfMetrics {
  lastScrollTime: number
  scrollCount: number
  rebuildCount: number
  layoutCount: number
}

export default defineComponent({
  name: 'VirtualScroller',
  props: {
    items: {
      type: Array as PropType<VirtualScrollerItem[]>,
      required: true
    },
    itemKey: {
      type: String,
      default: 'id'
    },
    estimatedItemHeight: {
      type: Number,
      default: 80
    },
    buffer: {
      type: Number,
      default: 5
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    remainingThreshold: {
      type: Number,
      default: 5
    },
    /**
     * Enable performance logging to console.
     * Logs scroll handler timing, prefix sum rebuilds, etc.
     */
    perfLog: {
      type: Boolean,
      default: false
    },
    /**
     * Use container scrolling instead of window scrolling.
     * When true, the component's container element will be the scroll target.
     * When false (default), uses window scroll events.
     */
    useContainerScroll: {
      type: Boolean,
      default: false
    }
  },
  emits: ['need-more'],
  data() {
    return {
      // === COMMITTED STATE (read during render - O(1)) ===
      // Single object for atomic updates - reduces Vue reactivity overhead
      // by triggering only ONE reactive notification instead of four
      committed: {
        startIndex: 0,
        endIndex: 15,
        topSpacer: 0,
        bottomSpacer: 0
      },

      // === MEASUREMENT STATE ===
      itemHeights: {} as ItemHeightCache, // index → measured height
      prefixSums: [0] as number[], // cumulative heights array
      averageItemHeight: this.estimatedItemHeight,
      viewportHeight: 0,
      scrollTop: 0,

      // === PENDING WORK ===
      // NON-REACTIVE by design: Map operations don't trigger Vue updates.
      // This is intentional - we only read this in RAF callbacks.
      pendingMeasurements: new Map<number, number>(),
      pendingMinIndex: Infinity, // track minimum changed index for incremental rebuild
      layoutUpdateScheduled: false, // debounce flag for layout updates
      measurementInProgress: false, // guard against re-measurement during same cycle

      // === SCROLL STATE ===
      lastScrollY: 0,
      lastScrollTime: 0,
      isRapidScrolling: false,

      // === OBSERVERS & RAF IDs ===
      resizeObserver: null as ResizeObserver | null,
      observedElements: new Set<Element>(),
      scrollRAFId: null as number | null,
      layoutRAFId: null as number | null,

      // === PERFORMANCE METRICS ===
      perfMetrics: {
        lastScrollTime: 0,
        scrollCount: 0,
        rebuildCount: 0,
        layoutCount: 0
      } as PerfMetrics
    }
  },
  computed: {
    /**
     * Visible items slice - O(1) read from committed state
     */
    visibleItems(): VirtualScrollerItem[] {
      return this.items.slice(this.committed.startIndex, this.committed.endIndex)
    },

    /**
     * Height of spacer above visible items - O(1) read from committed state
     * NO CALCULATIONS - just returns pre-computed value
     */
    topSpacerHeight(): number {
      return this.committed.topSpacer
    },

    /**
     * Height of spacer below visible items - O(1) read from committed state
     * NO CALCULATIONS - just returns pre-computed value
     */
    bottomSpacerHeight(): number {
      return this.committed.bottomSpacer
    },

    /**
     * Total scrollable height of all items - O(1) lookup
     */
    totalHeight(): number {
      return this.prefixSums[this.items.length] ?? 0
    },

    /**
     * Expose startIndex for template (reads committed state)
     */
    startIndex(): number {
      return this.committed.startIndex
    },

    /**
     * Expose endIndex for template (reads committed state)
     */
    endIndex(): number {
      return this.committed.endIndex
    },

    /**
     * Shorthand accessors for internal use
     */
    committedStartIndex(): number {
      return this.committed.startIndex
    },
    committedEndIndex(): number {
      return this.committed.endIndex
    }
  },
  watch: {
    items: {
      handler(newItems: VirtualScrollerItem[], oldItems?: VirtualScrollerItem[]) {
        // Clear pending measurements - indices may be invalid after items change
        this.pendingMeasurements.clear()
        this.pendingMinIndex = Infinity

        // Clear height cache for removed items if array shrunk
        if (oldItems && newItems.length < oldItems.length) {
          const oldLength = oldItems.length
          for (let i = newItems.length; i < oldLength; i++) {
            delete this.itemHeights[i]
          }
        }

        // Rebuild prefix sums and schedule layout update
        this.$nextTick(() => {
          this.rebuildPrefixSumsFrom(0)
          this.scheduleLayoutUpdate()
        })
      },
      deep: false
    }
  },
  mounted() {
    this.initializeViewport()
    this.setupResizeObserver()

    // Initial prefix sums build and layout
    this.rebuildPrefixSumsFrom(0)
    this.performLayoutUpdate()

    // Listen to scroll and resize events
    if (typeof window !== 'undefined') {
      if (this.useContainerScroll) {
        const container = this.$refs.scrollContainer as HTMLElement
        if (container) {
          container.addEventListener('scroll', this.handleScroll, { passive: true })
        }
      } else {
        window.addEventListener('scroll', this.handleScroll, { passive: true })
      }
      window.addEventListener('resize', this.handleResize, { passive: true })
    }

    if (this.perfLog) {
      console.log(
        `[VirtualScroller] mounted: ${this.items.length} items, ` +
          `estimatedHeight=${this.estimatedItemHeight}px, buffer=${this.buffer}`
      )
    }
  },
  beforeDestroy() {
    // Cancel pending RAF callbacks
    if (this.scrollRAFId !== null) {
      cancelAnimationFrame(this.scrollRAFId)
      this.scrollRAFId = null
    }

    if (this.layoutRAFId !== null) {
      cancelAnimationFrame(this.layoutRAFId)
      this.layoutRAFId = null
    }

    // Clear pending work
    this.pendingMeasurements.clear()

    // Cleanup ResizeObserver
    if (this.resizeObserver) {
      this.observedElements.forEach((element) => {
        this.resizeObserver?.unobserve(element)
      })
      this.observedElements.clear()
      this.resizeObserver.disconnect()
    }

    // Remove event listeners
    if (typeof window !== 'undefined') {
      if (this.useContainerScroll) {
        const container = this.$refs.scrollContainer as HTMLElement
        if (container) {
          container.removeEventListener('scroll', this.handleScroll)
        }
      } else {
        window.removeEventListener('scroll', this.handleScroll)
      }
      window.removeEventListener('resize', this.handleResize)
    }

    if (this.perfLog) {
      console.log(
        `[VirtualScroller] destroyed: ${this.perfMetrics.scrollCount} scroll events, ` +
          `${this.perfMetrics.rebuildCount} prefix sum rebuilds, ` +
          `${this.perfMetrics.layoutCount} layout updates`
      )
    }
  },
  methods: {
    // ============================================
    // PHASE 0: INITIALIZATION
    // ============================================

    initializeViewport(): void {
      if (typeof window !== 'undefined') {
        this.viewportHeight = window.innerHeight
      }
    },

    /**
     * Setup ResizeObserver with batched entry processing
     */
    setupResizeObserver(): void {
      if (typeof ResizeObserver === 'undefined') return

      // Single ResizeObserver instance processes all entries in one callback
      this.resizeObserver = new ResizeObserver((entries) => {
        this.handleResizeObserverEntries(entries)
      })
    },

    /**
     * Handle window resize events
     */
    handleResize(): void {
      if (typeof window !== 'undefined') {
        this.viewportHeight = window.innerHeight
        this.scheduleLayoutUpdate()
      }
    },

    // ============================================
    // PHASE 1: MEASUREMENT COLLECTION
    // ============================================

    /**
     * Process all ResizeObserver entries in a single batch.
     * Queues measurements and requests a single layout update.
     */
    handleResizeObserverEntries(entries: ResizeObserverEntry[]): void {
      for (const entry of entries) {
        const index = this.getIndexFromElement(entry.target as HTMLElement)
        if (index === null) continue

        // Get height from borderBoxSize (preferred) or offsetHeight (fallback)
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? (entry.target as HTMLElement).offsetHeight

        if (height <= 0) continue

        // Skip if no meaningful change (within 1px tolerance)
        const currentHeight = this.itemHeights[index]
        if (currentHeight !== undefined && Math.abs(currentHeight - height) < 1) continue

        // Queue measurement for batch processing
        this.pendingMeasurements.set(index, height)
        this.pendingMinIndex = Math.min(this.pendingMinIndex, index)
      }

      // Request single layout update for all measurements
      if (this.pendingMeasurements.size > 0) {
        this.scheduleLayoutUpdate()
      }
    },

    /**
     * Extract item index from element's data-index attribute
     */
    getIndexFromElement(element: HTMLElement): number | null {
      const dataIndex = element.getAttribute('data-index')
      if (dataIndex === null) return null
      const index = parseInt(dataIndex, 10)
      return isNaN(index) ? null : index
    },

    /**
     * Measure all currently visible items and queue height updates.
     * Called after DOM updates to measure newly rendered items.
     *
     * PERFORMANCE: Only measures items that haven't been measured yet.
     * Already-measured items are tracked via ResizeObserver for changes.
     */
    measureVisibleItems(): void {
      const container = this.$refs.scrollContainer as HTMLElement
      if (!container) return

      const children = container.querySelectorAll('.virtual-scroller-item')

      // First pass: collect indices that need measurement (avoid reading offsetHeight yet)
      const toMeasure: Array<{ element: HTMLElement; index: number }> = []

      children.forEach((child) => {
        const element = child as HTMLElement
        const index = this.getIndexFromElement(element)

        if (index !== null) {
          // Only measure if we don't have a cached height for this index
          // ResizeObserver handles updates to already-measured items
          if (this.itemHeights[index] === undefined) {
            toMeasure.push({ element, index })
          }

          // Always observe for future size changes
          if (this.resizeObserver && !this.observedElements.has(element)) {
            this.resizeObserver.observe(element)
            this.observedElements.add(element)
          }
        }
      })

      // Second pass: batch read all heights (single forced layout)
      if (toMeasure.length > 0) {
        for (const { element, index } of toMeasure) {
          const height = element.offsetHeight
          if (height > 0) {
            this.pendingMeasurements.set(index, height)
            this.pendingMinIndex = Math.min(this.pendingMinIndex, index)
          }
        }
      }

      this.cleanupObservedElements()

      // If we have pending measurements, schedule another layout update
      if (this.pendingMeasurements.size > 0) {
        this.scheduleLayoutUpdate()
      }
    },

    /**
     * Unobserve elements that are no longer in the visible range
     */
    cleanupObservedElements(): void {
      if (!this.resizeObserver) return

      const container = this.$refs.scrollContainer as HTMLElement
      if (!container) return

      // Get current visible element set
      const currentElements = new Set<Element>()
      const children = container.querySelectorAll('.virtual-scroller-item')
      children.forEach((child) => {
        currentElements.add(child)
      })

      // Unobserve elements that are no longer visible
      this.observedElements.forEach((element) => {
        if (!currentElements.has(element)) {
          this.resizeObserver?.unobserve(element)
          this.observedElements.delete(element)
        }
      })
    },

    // ============================================
    // PHASE 2: LAYOUT CALCULATION
    // ============================================

    /**
     * Schedule a layout update for the next animation frame.
     * Multiple calls within a frame are coalesced into one update.
     */
    scheduleLayoutUpdate(): void {
      if (this.layoutUpdateScheduled) return
      this.layoutUpdateScheduled = true

      this.layoutRAFId = requestAnimationFrame(() => {
        this.layoutRAFId = null
        this.layoutUpdateScheduled = false
        this.performLayoutUpdate()
      })
    },

    /**
     * Perform the actual layout update.
     * This is the ONLY place where layout state is calculated and committed.
     * Runs at most once per animation frame.
     */
    performLayoutUpdate(): void {
      const startTime = this.perfLog ? performance.now() : 0

      // Step 1: Apply pending measurements to height cache
      // IMPORTANT: We update the plain object directly (non-reactive) to avoid
      // triggering Vue's reactivity for each measurement. The prefix sums are
      // rebuilt from the cache, and committed state triggers the actual render.
      if (this.pendingMeasurements.size > 0) {
        let totalMeasured = 0
        let count = 0

        this.pendingMeasurements.forEach((height, index) => {
          // Direct assignment - itemHeights is only read during prefix sum rebuild
          // No need for Vue reactivity here since we control when it's read
          this.itemHeights[index] = height
          totalMeasured += height
          count++
        })

        // Update average with exponential moving average
        if (count > 0) {
          const newAvg = totalMeasured / count
          const alpha = Math.min(0.3, count / 20)
          this.averageItemHeight = this.averageItemHeight * (1 - alpha) + newAvg * alpha
        }

        this.pendingMeasurements.clear()
      }

      // Step 2: Rebuild prefix sums incrementally from minimum changed index
      if (this.pendingMinIndex < Infinity) {
        this.rebuildPrefixSumsFrom(this.pendingMinIndex)
        this.pendingMinIndex = Infinity
      }

      // Step 3: Calculate visible range using binary search
      const { startIndex, endIndex } = this.calculateVisibleRange()

      // Step 4: Calculate spacer heights
      const topSpacer = this.prefixSums[startIndex] ?? 0
      const totalHeight = this.prefixSums[this.items.length] ?? 0
      const endHeight = this.prefixSums[endIndex] ?? 0
      const bottomSpacer = Math.max(0, totalHeight - endHeight)

      // Step 5: ATOMIC COMMIT - update committed state as single object
      // This triggers only ONE reactive notification instead of four
      const rangeChanged =
        this.committed.startIndex !== startIndex || this.committed.endIndex !== endIndex

      this.committed = {
        startIndex,
        endIndex,
        topSpacer,
        bottomSpacer
      }

      if (this.perfLog) {
        this.perfMetrics.layoutCount++
        const elapsed = performance.now() - startTime
        console.log(
          `[VirtualScroller] performLayoutUpdate #${this.perfMetrics.layoutCount}: ` +
            `${elapsed.toFixed(2)}ms, range=[${startIndex}, ${endIndex}), ` +
            `spacers=[${topSpacer.toFixed(0)}, ${bottomSpacer.toFixed(0)}]`
        )
      }

      // Step 6: Measure newly rendered items after Vue updates DOM
      // Guard against re-measurement during same cycle
      if (rangeChanged && !this.measurementInProgress) {
        this.measurementInProgress = true
        this.$nextTick(() => {
          this.measurementInProgress = false
          this.measureVisibleItems()
        })
      }
    },

    /**
     * Calculate the visible range using binary search.
     * Returns start and end indices with buffer applied.
     * Time: O(log n)
     */
    calculateVisibleRange(): { startIndex: number; endIndex: number } {
      if (this.items.length === 0) {
        return { startIndex: 0, endIndex: 0 }
      }

      const container = this.$refs.scrollContainer as HTMLElement
      if (!container) {
        return { startIndex: 0, endIndex: Math.min(15, this.items.length) }
      }

      let scrollOffset: number
      let effectiveViewportHeight: number

      if (this.useContainerScroll) {
        // Container scroll: use container's scrollTop and clientHeight
        scrollOffset = container.scrollTop
        effectiveViewportHeight = container.clientHeight
      } else {
        // Window scroll: calculate offset relative to container position
        const containerRect = container.getBoundingClientRect()
        scrollOffset = Math.max(0, -containerRect.top)
        effectiveViewportHeight = this.viewportHeight
      }

      const viewportBottom = scrollOffset + effectiveViewportHeight

      // O(log n) binary search for visible range
      const firstVisible = this.findIndexAtOffset(scrollOffset)
      const lastVisible = this.findIndexAtOffset(viewportBottom)

      // Apply buffer
      return {
        startIndex: Math.max(0, firstVisible - this.buffer),
        endIndex: Math.min(this.items.length, lastVisible + 1 + this.buffer)
      }
    },

    // ============================================
    // PREFIX SUM MANAGEMENT
    // ============================================

    /**
     * Get the height for an item at the given index.
     * Returns measured height if available, otherwise estimated.
     * Time: O(1)
     */
    getHeightAt(index: number): number {
      if (index < 0 || index >= this.items.length) return 0
      return this.itemHeights[index] ?? this.averageItemHeight
    },

    /**
     * Rebuild prefix sums starting from a given index.
     * Incremental rebuild - O(n-k) where k is fromIndex.
     * Full rebuild when fromIndex is 0 - O(n).
     */
    rebuildPrefixSumsFrom(fromIndex: number): void {
      const startTime = this.perfLog ? performance.now() : 0

      const n = this.items.length

      // Ensure array is correct size
      if (this.prefixSums.length !== n + 1) {
        this.prefixSums = new Array<number>(n + 1)
        this.prefixSums[0] = 0
        fromIndex = 0
      }

      fromIndex = Math.max(0, fromIndex)

      // Rebuild from fromIndex to end
      for (let i = fromIndex; i < n; i++) {
        this.prefixSums[i + 1] = this.prefixSums[i] + this.getHeightAt(i)
      }

      if (this.perfLog) {
        this.perfMetrics.rebuildCount++
        const elapsed = performance.now() - startTime
        const itemsRebuilt = n - fromIndex
        console.log(
          `[VirtualScroller] rebuildPrefixSumsFrom(${fromIndex}): ${itemsRebuilt}/${n} items in ${elapsed.toFixed(
            2
          )}ms ` + `(rebuild #${this.perfMetrics.rebuildCount})`
        )
      }
    },

    // ============================================
    // BINARY SEARCH
    // ============================================

    /**
     * Binary search to find the index of the first item whose
     * bottom edge is > the given scroll offset.
     * Time: O(log n)
     *
     * @param offset - Scroll offset in pixels from top of container
     * @returns Index of the first visible item (before buffer applied)
     */
    findIndexAtOffset(offset: number): number {
      const n = this.items.length
      if (n === 0) return 0

      // Edge cases
      if (offset <= 0) return 0
      const totalHeight = this.prefixSums[n]
      if (offset >= totalHeight) return Math.max(0, n - 1)

      // Binary search: find smallest i where prefixSums[i+1] > offset
      let low = 0
      let high = n - 1

      while (low < high) {
        const mid = low + Math.floor((high - low) / 2)

        // prefixSums[mid + 1] is the bottom edge of item at index mid
        if (this.prefixSums[mid + 1] <= offset) {
          low = mid + 1
        } else {
          high = mid
        }
      }

      return low
    },

    // ============================================
    // SCROLL HANDLING
    // ============================================

    /**
     * Handle scroll events with RAF throttling and velocity detection.
     */
    handleScroll(): void {
      // Throttle to animation frame
      if (this.scrollRAFId !== null) return

      this.scrollRAFId = requestAnimationFrame(() => {
        this.scrollRAFId = null

        const startTime = this.perfLog ? performance.now() : 0
        const now = performance.now()

        const container = this.$refs.scrollContainer as HTMLElement
        const currentY = this.useContainerScroll ? container?.scrollTop ?? 0 : window.scrollY

        // Calculate scroll velocity
        const timeDelta = now - this.lastScrollTime
        const scrollDelta = Math.abs(currentY - this.lastScrollY)

        // Velocity in px/ms (Page Up/Down typically > 2 px/ms)
        const velocity = timeDelta > 0 ? scrollDelta / timeDelta : 0
        this.isRapidScrolling = velocity > 2

        this.lastScrollTime = now
        this.lastScrollY = currentY

        // Update scroll position
        if (this.useContainerScroll) {
          this.scrollTop = currentY
        } else if (container) {
          const containerTop = container.getBoundingClientRect().top + window.scrollY
          this.scrollTop = Math.max(0, window.scrollY - containerTop)
        }

        // Schedule layout update and check if more items needed
        this.scheduleLayoutUpdate()
        this.checkNeedMore()

        if (this.perfLog) {
          this.perfMetrics.scrollCount++
          const elapsed = performance.now() - startTime
          this.perfMetrics.lastScrollTime = elapsed

          // Log every 60 frames (~1 second at 60fps)
          if (this.perfMetrics.scrollCount % 60 === 0) {
            console.log(
              `[VirtualScroller] scroll #${this.perfMetrics.scrollCount}: ` +
                `${elapsed.toFixed(2)}ms, velocity=${velocity.toFixed(2)}px/ms, ` +
                `rapid=${this.isRapidScrolling}`
            )
          }
        }
      })
    },

    /**
     * Check if more items are needed and emit 'need-more' event.
     */
    checkNeedMore(): void {
      if (this.loadingMore) return

      const remainingItems = this.items.length - this.committedEndIndex
      if (remainingItems <= this.remainingThreshold) {
        this.$emit('need-more')
      }
    },

    // ============================================
    // PUBLIC API
    // ============================================

    /**
     * Programmatically scroll to bring a specific item into view.
     * Exposed for parent components via ref.
     *
     * @param index - The item index to scroll to
     * @param align - Where to align the item: 'start' | 'center' | 'end' (default: 'start')
     * @param behavior - Scroll behavior: 'smooth' | 'auto' (default: 'smooth')
     */
    scrollToIndex(
      index: number,
      align: 'start' | 'center' | 'end' = 'start',
      behavior: ScrollBehavior = 'smooth'
    ): void {
      // Bounds check
      if (index < 0 || index >= this.items.length) {
        if (this.perfLog) {
          console.warn(
            `[VirtualScroller] scrollToIndex: index ${index} out of bounds [0, ${this.items.length})`
          )
        }
        return
      }

      const container = this.$refs.scrollContainer as HTMLElement
      if (!container) return

      const containerTop = container.getBoundingClientRect().top + window.scrollY
      const itemTop = this.prefixSums[index] ?? 0
      const itemHeight = this.getHeightAt(index)

      let targetScroll: number

      switch (align) {
        case 'center':
          targetScroll = containerTop + itemTop - (this.viewportHeight - itemHeight) / 2
          break
        case 'end':
          targetScroll = containerTop + itemTop - this.viewportHeight + itemHeight
          break
        case 'start':
        default:
          targetScroll = containerTop + itemTop
          break
      }

      if (this.perfLog) {
        console.log(
          `[VirtualScroller] scrollToIndex(${index}, '${align}'): ` +
            `itemTop=${itemTop.toFixed(0)}px, targetScroll=${targetScroll.toFixed(0)}px`
        )
      }

      window.scrollTo({
        top: Math.max(0, targetScroll),
        behavior
      })
    },

    /**
     * Get the current scroll metrics. Useful for debugging/monitoring.
     */
    getScrollMetrics(): {
      startIndex: number
      endIndex: number
      visibleCount: number
      totalCount: number
      totalHeight: number
      measuredCount: number
      averageHeight: number
      isRapidScrolling: boolean
    } {
      return {
        startIndex: this.committedStartIndex,
        endIndex: this.committedEndIndex,
        visibleCount: this.committedEndIndex - this.committedStartIndex,
        totalCount: this.items.length,
        totalHeight: this.prefixSums[this.items.length] ?? 0,
        measuredCount: Object.keys(this.itemHeights).length,
        averageHeight: this.averageItemHeight,
        isRapidScrolling: this.isRapidScrolling
      }
    }
  }
})
